/**
 * Create Appointment With Bought Treatments Actor
 *
 * Creates an appointment with bought treatments via the Practice Companion API.
 * This actor calls the appointment-orchestrator endpoint to create an appointment
 * with the purchased treatment information.
 */

import { fromPromise } from "xstate";
import { z } from "zod";
import { trackNetworkRequest } from "../../lib/network-diagnostics";
import {
  type CreateAppointmentWithBoughtTreatmentsResult,
  createAppointmentWithBoughtTreatments,
} from "../../server-actions/appointment-actions";

/**
 * Input Schema (What this actor needs)
 */
const CreateAppointmentWithBoughtTreatmentsInputSchema = z.object({
  orgId: z.number(),
  practiceId: z.number(),
  consumerId: z.string(),
  hrt: z
    .object({
      hrtId: z.number(),
      customDuration: z.number().optional(),
    })
    .optional(),
  pricingMap: z
    .record(
      z.object({
        priceData: z.object({
          CASH: z
            .object({
              price: z.number(),
              pricedTreatmentId: z.number(),
            })
            .optional(),
          INSURED: z
            .object({
              price: z.number(),
              pricedTreatmentId: z.number(),
            })
            .optional(),
        }),
        mode: z.string(),
      }),
    )
    .optional(),
  // Explicit pricing mode from context (set by PAY_NOW event)
  // Preferred over pricingMap[hrtId].mode to avoid debounce race conditions
  pricingMode: z.enum(["CASH", "INSURED"]).optional(),
  pendingPricesMode: z.boolean().optional(),
  // Temporarily list of intake form IDs to associate with the appointment,
  // unitl backend can implement logic to determine this, tickets:
  // https://linear.app/healthcarerevolution/issue/PLA-1262/auto-attach-intake-for-hipaa-expiry-based-on-consumerhipaasigndate
  // https://linear.app/healthcarerevolution/issue/PLA-1257/kiosk-use-backend-intake-form-rules-instead-of-hardcoded-hipaa-expiry
  // passing empty array does not attach any intake forms,
  // passing undefined lets backend determine which intake forms to attach based on its own rules
  intakeFormIds: z.array(z.number()),
});

type CreateAppointmentWithBoughtTreatmentsInput = z.infer<
  typeof CreateAppointmentWithBoughtTreatmentsInputSchema
>;

/**
 * Success Schema (What this actor returns on success)
 */
const CreateAppointmentWithBoughtTreatmentsSuccessSchema = z.object({
  appointmentId: z.string(),
});

type CreateAppointmentWithBoughtTreatmentsSuccess = z.infer<
  typeof CreateAppointmentWithBoughtTreatmentsSuccessSchema
>;

/**
 * Error Schema (What this actor returns on failure)
 */
const CreateAppointmentWithBoughtTreatmentsErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "MISSING_HRT",
    "MISSING_PRICING",
    "API_ERROR",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
});

type CreateAppointmentWithBoughtTreatmentsError = z.infer<
  typeof CreateAppointmentWithBoughtTreatmentsErrorSchema
>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
  composedActors?: readonly string[];
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Create Appointment With Bought Treatments Actor Implementation
 */
const createAppointmentWithBoughtTreatmentsActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: CreateAppointmentWithBoughtTreatmentsInput;
  }): Promise<{ appointment: CreateAppointmentWithBoughtTreatmentsResult }> => {
    // Validate input
    const validated =
      CreateAppointmentWithBoughtTreatmentsInputSchema.safeParse(input);

    if (!validated.success) {
      const errorData: CreateAppointmentWithBoughtTreatmentsError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    const {
      orgId,
      practiceId,
      consumerId,
      hrt,
      pricingMap,
      pendingPricesMode,
    } = validated.data;

    // Check if HRT exists
    if (!hrt) {
      const errorData: CreateAppointmentWithBoughtTreatmentsError = {
        code: "MISSING_HRT",
        message: "No HRT selected for appointment",
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    // Get pricing information
    const hrtPricing = pricingMap?.[hrt.hrtId.toString()];
    if (!hrtPricing) {
      const errorData: CreateAppointmentWithBoughtTreatmentsError = {
        code: "MISSING_PRICING",
        message: `No pricing information found for HRT ${hrt.hrtId}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    // Get priced treatment ID from pricing map
    // Prefer explicit pricingMode from input (set by PAY_NOW) over pricingMap mode
    const mode = input.pricingMode ?? hrtPricing.mode ?? "CASH";
    const pricedTreatmentId = hrtPricing.priceData[mode]?.pricedTreatmentId;

    if (!pricedTreatmentId) {
      const errorData: CreateAppointmentWithBoughtTreatmentsError = {
        code: "MISSING_PRICING",
        message: `No priced treatment ID found for mode ${mode}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    // Calculate start and end times based on custom duration
    const now = new Date();
    const durationMinutes = hrt.customDuration || 15; // Default to 15 minutes
    const endTime = new Date(now.getTime() + durationMinutes * 60000);

    const tracker = trackNetworkRequest(
      "createAppointmentWithBoughtTreatments",
      {
        orgId,
        practiceId: String(practiceId),
      },
    );

    try {
      const result = await createAppointmentWithBoughtTreatments({
        orgId,
        practiceId,
        consumerId,
        pricedTreatmentId,
        paymentType: mode === "INSURED" ? "INSURANCE" : "SELFPAY",
        startTime: now.toISOString(),
        endTime: endTime.toISOString(),
        reviewStatus:
          pendingPricesMode && mode === "INSURED"
            ? "NEEDS_ADMIN_CREATED"
            : undefined,
        hrtId: hrt.hrtId,
        intakeFormIds: validated.data.intakeFormIds,
      });

      tracker.success();

      console.log(
        "[createAppointmentWithBoughtTreatmentsActor] Successfully created appointment:",
        result,
      );

      return { appointment: { ...result.appointment } };
    } catch (error) {
      tracker.error(error);
      const errorData: CreateAppointmentWithBoughtTreatmentsError = {
        code: "API_ERROR",
        message: error instanceof Error ? error.message : "Unknown error",
        retryable: true,
      };
      console.log(
        "[createAppointmentWithBoughtTreatmentsActor] Error:",
        errorData,
      );
      const apiError = new Error(errorData.message);
      Object.assign(apiError, errorData);
      throw apiError;
    }
  },
);

/**
 * Create Appointment With Bought Treatments Actor with Metadata
 */
export const createAppointmentWithBoughtTreatmentsActor: ActorWithMetadata = {
  impl: createAppointmentWithBoughtTreatmentsActorImpl,
  metadata: {
    displayName: "createAppointmentWithBoughtTreatments",
    description:
      "Create appointment with bought treatments via appointment orchestrator",
    category: "data",
    retryable: true,
    timeout: 15000, // 15 seconds
    errorCodes: [
      "INVALID_INPUT",
      "MISSING_HRT",
      "MISSING_PRICING",
      "API_ERROR",
      "UNKNOWN_ERROR",
    ],
    inputSchema: CreateAppointmentWithBoughtTreatmentsInputSchema,
    successSchema: CreateAppointmentWithBoughtTreatmentsSuccessSchema,
    errorSchema: CreateAppointmentWithBoughtTreatmentsErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as CreateAppointmentWithBoughtTreatmentsSuccess;
      return {
        appointmentId: success.appointmentId,
        appointmentCreated: true,
      };
    },
    onError: (error: unknown) => {
      const err = error as CreateAppointmentWithBoughtTreatmentsError;
      return {
        appointmentCreated: false,
        appointmentCreationError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
